import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

// Self-heal stale tabs after a deploy: when a code-split chunk hash no
// longer exists (a new deploy ran `s3 sync --delete`), an open tab throws
// ChunkLoadError on lazy import and white-screens. Force a one-time reload
// to pull the fresh index.html (served no-store) + current chunks.
function isChunkLoadError(msg?: string | null): boolean {
  if (!msg) return false;
  return (
    /Loading (?:CSS )?chunk [\w-]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}
function recoverFromStaleChunks(msg?: string | null): void {
  if (!isChunkLoadError(msg)) return;
  const KEY = 'tv_chunk_reload_at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 30_000) return; // already tried — don't loop
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
}
window.addEventListener('error', (e) =>
  recoverFromStaleChunks(e?.message || (e?.error ? String(e.error) : null)),
);
window.addEventListener('unhandledrejection', (e) => {
  const r = (e as PromiseRejectionEvent)?.reason;
  recoverFromStaleChunks(typeof r === 'string' ? r : r?.message);
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60_000,     // 5 min before refetch
      gcTime: 15 * 60_000,       // Evict unused cache entries after 15 min
    },
  },
});

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
